
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
/* eslint-disable @typescript-eslint/no-var-requires */
const VueP5 = require('vue-p5');
const pencilIcon = require('../../../assets/icons/whiteboard/pencil.png');
const pointerIcon = require('../../../assets/icons/whiteboard/pointer.png');
const magicPenIcon = require('../../../assets/icons/whiteboard/magicpen.png');
const eraserIcon = require('../../../assets/icons/whiteboard/eraser.png');
import {
  LINE_WIDTH_MAGICPEN,
  LINE_WIDTH_PENCIL,
  RUBBER_SIZE,
  WhiteboardMode,
  WhiteboardPenColors,
} from '@/utils/whiteboard';
import { DataService } from '@/service/DataService';
import {
  ELEMENT_ID_DRAGGABLE_VIEWPORT,
  ELEMENT_ID_WHITEBOARD,
  MAX_SCALE_ON_PINCH,
  ROOM_FULL_SPACE_HEIGHT,
  ROOM_FULL_SPACE_WIDTH,
  SCALE_ON_WHEEL_SPEED,
} from '@/utils/constants';
import {
  getMinScale,
  getCoordinatesPositionWithoutScale,
  getTranslateViewportAfterZoom,
  getViewportSize,
} from '@/utils/functions';
import { CoordinatesPosition, DrawMessage, EraseMessage } from '@/utils/models';

@Component({ components: { VueP5 } })
export default class Whiteboard extends Vue {
  @Prop() private isDrawingMode!: boolean;
  @Prop() private participantsPresent!: boolean;
  @Prop() private dataService!: DataService;
  @Prop() private drawingMode!: WhiteboardMode;
  @Prop() private chosenColor!: string;

  get roomScale(): number {
    return this.$store.state.roomScale;
  }
  get viewportTranslateValues(): CoordinatesPosition {
    return this.$store.state.viewportTranslateValues;
  }

  @Watch('drawingMode')
  private onDrawingModeChanged(): void {
    this.changeCursor();
  }

  private readonly WHITEBOARD_ID = ELEMENT_ID_WHITEBOARD;

  private whiteboardElement: HTMLDivElement | null = null;
  private canvasElement: HTMLCanvasElement | null = null;
  private viewportElement: HTMLDivElement | null = null;
  private p5SketchRef: any;
  private currentCursor: any = pointerIcon;
  private scale = 1;

  private mounted(): void {
    this.whiteboardElement = document.getElementById(ELEMENT_ID_WHITEBOARD) as HTMLDivElement;
    this.setViewportElementRef();
    this.addZoom();
    this.changeCursor();
  }

  private setViewportElementRef(): void {
    this.viewportElement = document.getElementById(ELEMENT_ID_DRAGGABLE_VIEWPORT) as HTMLDivElement;
  }

  private addZoom(): void {
    if (this.whiteboardElement) {
      this.canvasElement = this.whiteboardElement.firstChild as HTMLCanvasElement;

      if (this.canvasElement) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;

        const eventCache: Array<PointerEvent> = [];
        let prevDiff = -1;
        this.canvasElement.onpointerdown = function (e: PointerEvent) {
          eventCache.push(e);
        };

        this.canvasElement.onpointermove = function (e: PointerEvent) {
          for (let i = 0; i < eventCache.length; i++) {
            if (e.pointerId == eventCache[i].pointerId) {
              eventCache[i] = e;
              break;
            }
          }

          if (eventCache.length == 2) {
            const curDiff: number = Math.abs(eventCache[0].clientX - eventCache[1].clientX);

            if (prevDiff > 0) {
              const scaleSpeed = 0.01;
              const scaleNormalized = (curDiff / MAX_SCALE_ON_PINCH) * scaleSpeed;

              const previousScale = that.scale;
              const sign = curDiff < prevDiff ? 1 : -1;
              that.scale += sign * -scaleNormalized;

              const MinScale = getMinScale(that.viewportElement);

              if (that.scale > 1) {
                that.scale = 1;
              } else if (that.scale < MinScale) {
                that.scale = MinScale;
              }
              const viewportSize = getViewportSize(that.viewportElement as HTMLDivElement);
              const newTranslateViewport = getTranslateViewportAfterZoom(
                previousScale,
                that.scale,
                that.viewportTranslateValues,
                viewportSize,
              );

              that.$store.commit('SetViewportTranslateValues', newTranslateViewport);
              that.$store.commit('SetRoomScale', that.scale);
            }

            prevDiff = curDiff;
          }
        };

        this.canvasElement.onpointerup = function (event: PointerEvent) {
          for (let i = 0; i < eventCache.length; i++) {
            if (eventCache[i].pointerId == event.pointerId) {
              eventCache.splice(i, 1);
              break;
            }
          }

          if (eventCache.length < 2) {
            prevDiff = -1;
          }
        };

        this.canvasElement.onwheel = (e: WheelEvent) => {
          e.preventDefault();

          const previousScale = that.scale;
          const sign = e.deltaY > 0 ? 1 : -1;
          that.scale += sign * -SCALE_ON_WHEEL_SPEED;

          const MinScale = getMinScale(that.viewportElement);

          if (that.scale > 1) {
            that.scale = 1;
          } else if (that.scale < MinScale) {
            that.scale = MinScale;
          }
          const viewportSize = getViewportSize(that.viewportElement as HTMLDivElement);
          const newTranslateViewport = getTranslateViewportAfterZoom(
            previousScale,
            that.scale,
            that.viewportTranslateValues,
            viewportSize,
          );

          that.$store.commit('SetViewportTranslateValues', newTranslateViewport);
          that.$store.commit('SetRoomScale', that.scale);
        };
      }
    }
  }

  private setup(sketch: any) {
    this.p5SketchRef = sketch;
    sketch.createCanvas(ROOM_FULL_SPACE_WIDTH, ROOM_FULL_SPACE_HEIGHT);
  }

  private draw(sketch: any) {
    if (this.isDrawingMode) {
      if (this.drawingMode === 'pencilMode' || this.drawingMode === 'magicpenMode') {
        const color = (WhiteboardPenColors as any)[this.chosenColor];
        const strokeWeight: number = this.drawingMode === 'pencilMode' ? LINE_WIDTH_PENCIL : LINE_WIDTH_MAGICPEN;
        const start = {
          x: sketch.pmouseX,
          y: sketch.pmouseY,
        };
        const startWithoutScale = getCoordinatesPositionWithoutScale(start, this.roomScale);
        const end = {
          x: sketch.mouseX,
          y: sketch.mouseY,
        };
        const endWithoutScale = getCoordinatesPositionWithoutScale(end, this.roomScale);
        this.drawLine(color, strokeWeight, startWithoutScale, endWithoutScale);
        this.dataService.emitDraw(startWithoutScale, endWithoutScale, color, strokeWeight);
      } else if (this.drawingMode === 'erase') {
        const erasePosition: CoordinatesPosition = {
          x: sketch.mouseX,
          y: sketch.mouseY,
        };
        const erasePositionWithoutScale: CoordinatesPosition = getCoordinatesPositionWithoutScale(
          erasePosition,
          this.roomScale,
        );
        this.erase(erasePositionWithoutScale);
        this.dataService.emitErase(erasePositionWithoutScale);
      }
    }
  }

  public onWhiteboardStateHistoryReceived(whiteboardHistory: Array<DrawMessage | EraseMessage>): void {
    whiteboardHistory.forEach((drawingStep: DrawMessage | EraseMessage) => {
      if (drawingStep.type === 'draw') {
        this.onDrawnLineMessageReceived(drawingStep as DrawMessage);
      } else if (drawingStep.type === 'erase') {
        this.onEraseMessageReceived(drawingStep as EraseMessage);
      }
    });
  }

  public onDrawnLineMessageReceived(payload: DrawMessage): void {
    this.drawLine(payload.strokeColor, payload.strokeWeight, payload.coordinates.start, payload.coordinates.end);
  }

  public onEraseMessageReceived(payload: EraseMessage): void {
    this.erase(payload.coordinates);
  }

  private erase(erasePositionWithoutScale: CoordinatesPosition): void {
    this.p5SketchRef.erase();
    this.p5SketchRef.ellipse(erasePositionWithoutScale.x, erasePositionWithoutScale.y, RUBBER_SIZE, RUBBER_SIZE);
  }

  private drawLine(color: string, strokeWeight: number, start: CoordinatesPosition, end: CoordinatesPosition): void {
    this.p5SketchRef.noErase();
    this.p5SketchRef.stroke(color);
    this.p5SketchRef.strokeWeight(strokeWeight);
    this.p5SketchRef.line(start.x, start.y, end.x, end.y);
  }

  private changeCursor(): void {
    if (this.drawingMode === 'pencilMode') {
      this.currentCursor = pencilIcon;
    } else if (this.drawingMode === 'magicpenMode') {
      this.currentCursor = magicPenIcon;
    } else if (this.drawingMode === 'erase') {
      this.currentCursor = eraserIcon;
    } else {
      this.currentCursor = pointerIcon;
    }

    if (this.whiteboardElement) {
      this.whiteboardElement.style.cursor = "url('" + this.currentCursor + "') 6 32, auto";
    }
  }
}
